import { useCallback, useEffect, useRef, useState } from 'react';

import {
  buildPendingReportActionRows,
  buildReportActionRowsFromError,
  buildReportActionRowsFromResult,
} from './reportActionStatusAdapter';

const INITIAL_ACTION_STATUS_STATE = {
  open: false,
  actionType: null,
  rows: [],
  inFlight: false,
  recompute: null,
};

const buildReportActionErrorMessage = (action, actionScope = 'selected') => {
  const target = action.type === 'push_to_vmt' ? 'VMT' : 'DI';
  const noun = actionScope === 'row' ? 'row' : 'selected rows';
  return `Failed to ${action.type === 'push_to_vmt' ? 'send' : 'sync'} ${noun} to ${target}.`;
};

const buildReportActionResultMessage = (action, result = {}) => {
  const processedCount = result.sentCount ?? result.syncedCount ?? 0;
  const failedCount = Number.isFinite(result.failedCount) ? result.failedCount : 0;
  const skippedCount = Number.isFinite(result.skippedCount) ? result.skippedCount : 0;
  const attemptedCount = Number.isFinite(result.attemptedCount)
    ? result.attemptedCount
    : processedCount + skippedCount;
  const acceptedCount = Number.isFinite(result.acceptedCount)
    ? result.acceptedCount
    : Math.max(processedCount - failedCount, 0);
  const verb = action.type === 'push_to_vmt' ? 'Sent' : 'Synced';
  const target = action.type === 'push_to_vmt' ? 'to VMT' : 'to DI';
  const recomputeFailed = result.recompute && result.recompute.ok === false;
  const hasFailures = failedCount > 0;

  return {
    severity: recomputeFailed || hasFailures ? 'warning' : 'success',
    message: recomputeFailed
      ? `${verb} ${acceptedCount} of ${attemptedCount} row(s) ${target}, but report recompute failed. Use the manual recompute fallback before continuing.`
      : `${verb} ${acceptedCount} of ${attemptedCount} row(s) ${target}${failedCount ? ` (${failedCount} failed)` : ''}${skippedCount ? ` (${skippedCount} skipped)` : ''}.`,
  };
};

const useReportActionStatusFlow = ({
  reportId,
  reportAction,
  queryClient,
  setSelectionModel,
  showSnackbar,
  resolveServerErrorMessage,
}) => {
  const [actionStatusState, setActionStatusState] = useState(INITIAL_ACTION_STATUS_STATE);
  const operationTokenRef = useRef(0);
  const reportIdRef = useRef(reportId);
  reportIdRef.current = reportId;

  useEffect(() => {
    operationTokenRef.current += 1;
    reportIdRef.current = reportId;
    setActionStatusState(INITIAL_ACTION_STATUS_STATE);
  }, [reportId]);

  const closeActionStatus = useCallback(() => {
    setActionStatusState((prev) => ({ ...prev, open: false }));
  }, []);

  const clearActionStatusSelection = useCallback(() => {
    setSelectionModel([]);
  }, [setSelectionModel]);

  const selectActionStatusRows = useCallback((ids) => {
    setSelectionModel(ids);
  }, [setSelectionModel]);

  const handleReportActionResult = useCallback(
    (action, result, { clearSelection = false } = {}) => {
      if (action.type === 'push_to_vmt' || result?.snapshot) {
        queryClient.invalidateQueries({ queryKey: ['vmtSendHistory'] });
      }
      if (clearSelection) {
        setSelectionModel([]);
      }
      const snackbar = buildReportActionResultMessage(action, result);
      showSnackbar(snackbar.message, snackbar.severity);
    },
    [queryClient, setSelectionModel, showSnackbar]
  );

  const runReportSendAction = useCallback(
    async ({ action, rows = [], row = null, actionScope = 'selected', clearSelection = false }) => {
      const operationToken = operationTokenRef.current + 1;
      operationTokenRef.current = operationToken;
      const actionReportId = reportId;
      const isCurrentOperation = () => (
        operationTokenRef.current === operationToken && reportIdRef.current === actionReportId
      );
      const sourceRows = row ? [row] : rows;
      setActionStatusState({
        open: true,
        actionType: action.type,
        rows: buildPendingReportActionRows(sourceRows),
        inFlight: true,
        recompute: null,
      });
      try {
        const result = await reportAction.mutateAsync({
          reportId,
          actionType: action.type,
          action,
          row: row || undefined,
          rows: row ? undefined : rows,
        });
        if (!isCurrentOperation()) return;
        setActionStatusState({
          open: true,
          actionType: action.type,
          rows: buildReportActionRowsFromResult({ sourceRows, result }),
          inFlight: false,
          recompute: result?.recompute || null,
        });
        handleReportActionResult(action, result, { clearSelection });
      } catch (error) {
        if (!isCurrentOperation()) return;
        const fallbackMessage = buildReportActionErrorMessage(action, actionScope);
        setActionStatusState({
          open: true,
          actionType: action.type,
          rows: buildReportActionRowsFromError({
            sourceRows,
            error,
            fallbackMessage,
          }),
          inFlight: false,
          recompute: null,
        });
        showSnackbar(resolveServerErrorMessage(error, fallbackMessage), 'error');
      }
    },
    [handleReportActionResult, reportAction, reportId, resolveServerErrorMessage, showSnackbar]
  );

  return {
    actionStatusState,
    closeActionStatus,
    clearActionStatusSelection,
    selectActionStatusRows,
    runReportSendAction,
  };
};

export {
  INITIAL_ACTION_STATUS_STATE,
  buildReportActionErrorMessage,
  buildReportActionResultMessage,
};

export default useReportActionStatusFlow;
